// מערכת ניהול פרויקטים — הוספה, עריכה ומחיקה של פרויקטים ותמונות.
// כל שמירה נשמרת ישירות ל-GitHub והאתר מתעדכן אוטומטית תוך 2-3 דקות.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { API_URL } from '../../config';

// --- המרת HTML של תיאור מלא לטקסט פשוט לעריכה, ובחזרה ---
const htmlToEditable = (html = '') => html
    .replace(/<strong>([\s\S]*?)<\/strong>/g, '**$1**')
    .replace(/<li>([\s\S]*?)<\/li>/g, '* $1\n')
    .replace(/<\/?ul>/g, '\n')
    .replace(/<p>([\s\S]*?)<\/p>/g, '$1\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const escapeHtml = (t) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const inlineHtml = (t) => escapeHtml(t).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

const editableToHtml = (text) => {
    let html = '';
    let bullets = [];
    const flushBullets = () => {
        if (bullets.length) {
            html += `<ul>${bullets.map((b) => `<li>${inlineHtml(b)}</li>`).join('')}</ul>`;
            bullets = [];
        }
    };
    for (const rawLine of text.split('\n')) {
        const line = rawLine.trim();
        if (!line) { flushBullets(); continue; }
        if (/^\*(?!\*)/.test(line)) {
            bullets.push(line.replace(/^\*\s*/, ''));
        } else {
            flushBullets();
            html += `<p>${inlineHtml(line)}</p>`;
        }
    }
    flushBullets();
    return html;
};

// הקטנת תמונה בדפדפן לפני העלאה — חוסך נפח ומזרז את האתר
const readAndResizeImage = (file, maxDim = 1600, quality = 0.78) => new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        URL.revokeObjectURL(url);
        resolve({ name: file.name, dataUrl, dataBase64: dataUrl.split(',')[1] });
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error(`לא ניתן לקרוא את ${file.name}`)); };
    img.src = url;
});

const emptyForm = { id: null, title: '', shortDescription: '', description: '' };

function ProjectsManager() {
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    // מצב עריכה
    const [editing, setEditing] = useState(null); // null = רשימה, אובייקט = טופס
    const [form, setForm] = useState(emptyForm);
    const [existingImages, setExistingImages] = useState([]); // [{name, deleted}]
    const [newImages, setNewImages] = useState([]); // [{name, dataUrl, dataBase64}]
    const [mainImageName, setMainImageName] = useState(null);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState({ state: 'idle', message: '' });

    const token = localStorage.getItem('accessToken');

    const authFetch = useCallback((path, options = {}) => fetch(`${API_URL}${path}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...options.headers,
        },
    }), [token]);

    const loadProjects = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        try {
            const res = await authFetch('/api/admin/site-projects');
            if (res.status === 401 || res.status === 403) { navigate('/admin/login'); return; }
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'שגיאה בטעינה');
            setProjects(data);
        } catch (err) {
            setLoadError(err.message);
        } finally {
            setLoading(false);
        }
    }, [authFetch, navigate]);

    useEffect(() => {
        if (!token) { navigate('/admin/login'); return; }
        loadProjects();
    }, [token, navigate, loadProjects]);

    const openEditor = (project) => {
        if (project) {
            setForm({
                id: project.id,
                title: project.title,
                shortDescription: project.shortDescription || '',
                description: htmlToEditable(project.fullDescription || ''),
            });
            setExistingImages((project.images || []).map((name) => ({ name, deleted: false })));
            const currentMain = project.mainImage ? project.mainImage.split('/').pop() : null;
            setMainImageName(currentMain);
            setEditing({ id: project.id, folder: project.folder, title: project.title });
        } else {
            setForm(emptyForm);
            setExistingImages([]);
            setMainImageName(null);
            setEditing({ id: null, folder: null, title: 'פרויקט חדש' });
        }
        setNewImages([]);
        setStatus({ state: 'idle', message: '' });
    };

    const handleFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        if (files.length === 0) return;
        setStatus({ state: 'sending', message: `מעבד ${files.length} תמונות...` });
        try {
            const processed = [];
            for (const file of files) {
                processed.push(await readAndResizeImage(file));
            }
            setNewImages((prev) => [...prev, ...processed]);
            setStatus({ state: 'idle', message: '' });
        } catch (err) {
            setStatus({ state: 'error', message: err.message });
        }
    };

    const toggleDeleteExisting = (name) => {
        setExistingImages((prev) => prev.map((img) =>
            img.name === name ? { ...img, deleted: !img.deleted } : img
        ));
        if (mainImageName === name) setMainImageName(null);
    };

    const removeNewImage = (name) => {
        setNewImages((prev) => prev.filter((img) => img.name !== name));
        if (mainImageName === name) setMainImageName(null);
    };

    const handleSave = async () => {
        if (!form.title.trim()) {
            setStatus({ state: 'error', message: 'חסר שם פרויקט' });
            return;
        }
        setSaving(true);
        setStatus({ state: 'sending', message: 'שומר ומעלה ל-GitHub... (עד דקה)' });
        try {
            const res = await authFetch('/api/admin/site-projects', {
                method: 'POST',
                body: JSON.stringify({
                    project: {
                        id: form.id || undefined,
                        title: form.title,
                        shortDescription: form.shortDescription,
                        fullDescription: editableToHtml(form.description),
                    },
                    newImages: newImages.map(({ name, dataBase64 }) => ({ name, dataBase64 })),
                    deleteImages: existingImages.filter((i) => i.deleted).map((i) => i.name),
                    mainImageName,
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'השמירה נכשלה');
            setStatus({ state: 'success', message: data.message });
            setEditing(null);
            await loadProjects();
        } catch (err) {
            setStatus({ state: 'error', message: err.message });
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteProject = async (project) => {
        if (!window.confirm(`למחוק את "${project.title}" כולל כל התמונות? אי אפשר לבטל.`)) return;
        setStatus({ state: 'sending', message: 'מוחק...' });
        try {
            const res = await authFetch(`/api/admin/site-projects/${project.id}`, { method: 'DELETE' });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'המחיקה נכשלה');
            setStatus({ state: 'success', message: data.message });
            await loadProjects();
        } catch (err) {
            setStatus({ state: 'error', message: err.message });
        }
    };

    // --- טופס עריכה ---
    if (editing) {
        const visibleExisting = existingImages.filter(() => true);
        return (
            <div id="main">
                <section className="post admin-manager">
                    <header className="major" style={{ textAlign: 'center' }}>
                        <h1>{editing.id ? `עריכת: ${editing.title}` : 'פרויקט חדש'}</h1>
                    </header>

                    <div className="fields">
                        <div className="field">
                            <label>שם הפרויקט</label>
                            <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="לדוגמה: בית פרטי בגן יבנה" />
                        </div>
                        <div className="field">
                            <label>תיאור קצר (מוצג בעמוד הפרויקטים)</label>
                            <textarea rows="2" value={form.shortDescription} onChange={(e) => setForm({ ...form, shortDescription: e.target.value })} placeholder="משפט אחד על הפרויקט" />
                        </div>
                        <div className="field">
                            <label>תיאור מלא (כל שורה = פסקה; שורה שמתחילה ב-* הופכת לנקודה; **טקסט** = מודגש)</label>
                            <textarea rows="8" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={'ספרו על הפרויקט...\n* נקודה ראשונה\n* נקודה שנייה'} />
                        </div>
                    </div>

                    <h2>תמונות</h2>
                    <p className="admin-hint">לחצו על ⭐ כדי לבחור תמונה ראשית (התמונה שמייצגת את הפרויקט באתר). לחצו על 🗑 כדי לסמן תמונה למחיקה.</p>

                    <div className="admin-images-grid">
                        {visibleExisting.map((img) => (
                            <div key={img.name} className={`admin-image-card${img.deleted ? ' is-deleted' : ''}${mainImageName === img.name ? ' is-main' : ''}`}>
                                <img src={`/images/${editing.folder}/${img.name}`} alt={img.name} loading="lazy" />
                                <div className="admin-image-actions">
                                    <button type="button" title="בחר כתמונה ראשית" disabled={img.deleted} onClick={() => setMainImageName(img.name)}>
                                        {mainImageName === img.name ? '⭐ ראשית' : '☆'}
                                    </button>
                                    <button type="button" title={img.deleted ? 'בטל מחיקה' : 'מחק תמונה'} onClick={() => toggleDeleteExisting(img.name)}>
                                        {img.deleted ? '↩ שחזר' : '🗑'}
                                    </button>
                                </div>
                            </div>
                        ))}
                        {newImages.map((img) => (
                            <div key={img.name} className={`admin-image-card is-new${mainImageName === img.name ? ' is-main' : ''}`}>
                                <img src={img.dataUrl} alt={img.name} />
                                <span className="admin-image-badge">חדש</span>
                                <div className="admin-image-actions">
                                    <button type="button" title="בחר כתמונה ראשית" onClick={() => setMainImageName(img.name)}>
                                        {mainImageName === img.name ? '⭐ ראשית' : '☆'}
                                    </button>
                                    <button type="button" title="הסר" onClick={() => removeNewImage(img.name)}>🗑</button>
                                </div>
                            </div>
                        ))}
                        <label className="admin-upload-tile">
                            <input type="file" accept="image/*" multiple onChange={handleFiles} style={{ display: 'none' }} />
                            <span>+ הוספת תמונות</span>
                        </label>
                    </div>

                    <div className="admin-actions">
                        <button type="button" className="button primary" disabled={saving} onClick={handleSave}>
                            {saving ? 'שומר...' : 'שמירה ופרסום לאתר'}
                        </button>
                        <button type="button" className="button" disabled={saving} onClick={() => { setEditing(null); setStatus({ state: 'idle', message: '' }); }}>
                            ביטול
                        </button>
                    </div>
                    {status.message && <p className={`form-status form-status-${status.state}`}>{status.message}</p>}
                </section>
            </div>
        );
    }

    // --- רשימת פרויקטים ---
    return (
        <div id="main">
            <section className="post admin-manager">
                <header className="major" style={{ textAlign: 'center' }}>
                    <h1>ניהול פרויקטים</h1>
                    <p>הוסיפו, ערכו ומחקו פרויקטים — האתר מתעדכן אוטומטית תוך 2-3 דקות מכל שמירה.</p>
                </header>

                <div className="admin-toolbar">
                    <button type="button" className="button primary" onClick={() => openEditor(null)}>+ פרויקט חדש</button>
                    <Link to="/admin/contacts" className="button">לפניות שהתקבלו</Link>
                </div>

                {status.message && <p className={`form-status form-status-${status.state}`}>{status.message}</p>}
                {loading && <p>טוען פרויקטים...</p>}
                {loadError && (
                    <div className="form-status form-status-error">
                        <strong>שגיאה:</strong> {loadError}
                        {loadError.includes('GITHUB_TOKEN') && (
                            <p style={{ margin: '0.5rem 0 0' }}>נדרשת הגדרה חד-פעמית בשרת — ראו את הקובץ ADMIN-SETUP.md בפרויקט.</p>
                        )}
                    </div>
                )}

                <div className="admin-projects-list">
                    {projects.map((project) => (
                        <div key={project.id} className="admin-project-row">
                            <img
                                src={project.mainImage || `/images/${project.folder}/${(project.images || [])[0] || ''}`}
                                alt={project.title}
                                loading="lazy"
                            />
                            <div className="admin-project-info">
                                <h3>{project.title}</h3>
                                <small>{(project.images || []).length} תמונות</small>
                            </div>
                            <div className="admin-project-actions">
                                <button type="button" className="button" onClick={() => openEditor(project)}>עריכה</button>
                                <button type="button" className="button admin-delete" onClick={() => handleDeleteProject(project)}>מחיקה</button>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}

export default ProjectsManager;
